import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Pagination } from '@/components/ui/Pagination';
import { TABLE_BAR_DENSITY, TD_DENSITY, TH_DENSITY } from '@/components/ui/tableDensity';
import { useToast } from '@/components/ui/useToast';
import { apiError } from '@/lib/apiError';
import { exportReport, useOnSignal, xl, type ExportCell } from './exportReport';

export interface RegisterCol<T> {
  key: string;
  header: string;
  align?: 'left' | 'right' | 'center';
  cell: (row: T) => ReactNode;
  /** Value written to Excel; defaults to the row's field named `key`. */
  value?: (row: T) => unknown;
}

const HEAD = 'border-b-2 border-r border-[var(--border-color)] bg-orange-50 font-black uppercase tracking-wide text-accent whitespace-nowrap last:border-r-0 ' + TH_DENSITY;
const CELL = 'border-r border-[var(--border-color)] font-semibold text-[var(--text-primary)] whitespace-nowrap last:border-r-0 ' + TD_DENSITY;

/**
 * ERP-themed report table (matches SIS Stock / Customer OS): orange header
 * band, bordered grid, striped rows, and honoured per-column alignment so
 * money columns right-align. Client-side search via `searchText`.
 */
export function RegisterTable<T>({
  title,
  count,
  columns,
  rows,
  rowKey,
  loading,
  search = '',
  searchText,
  exportSignal,
  exportName,
}: {
  title: string;
  count?: string;
  columns: RegisterCol<T>[];
  rows: T[];
  rowKey: (row: T) => string | number;
  loading?: boolean;
  search?: string;
  searchText?: (row: T) => string;
  /** Bumped by the page's Export button; exports every row matching filters + search. */
  exportSignal?: number;
  /** File / sheet name for the export, e.g. "Sales_Register_By_Customer". */
  exportName?: string;
}) {
  const shown = useMemo(() => {
    const terms = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (!terms.length || !searchText) return rows;
    return rows.filter((r) => {
      const hay = searchText(r).toLowerCase();
      return terms.every((t) => hay.includes(t));
    });
  }, [rows, search, searchText]);

  const toast = useToast();
  useOnSignal(exportSignal, () => {
    if (!shown.length) { toast.push('Nothing to export for these filters.', 'info'); return; }
    const headers = columns.map((c) => c.header);
    const rows: ExportCell[][] = shown.map((r) =>
      columns.map((c) => xl(c.value ? c.value(r) : (r as Record<string, unknown>)[c.key])),
    );
    exportReport(exportName ?? title.replace(/\W+/g, '_'), title, headers, rows)
      .catch((e) => toast.push(apiError(e, 'Export failed'), 'error'));
  });

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  useEffect(() => { setPage(1); }, [search, shown.length]);
  const total = shown.length;
  const lastPage = Math.max(1, Math.ceil(total / pageSize));
  const pageSafe = Math.min(page, lastPage);
  const paged = useMemo(() => shown.slice((pageSafe - 1) * pageSize, pageSafe * pageSize), [shown, pageSafe, pageSize]);

  // Cap the scroll body so the card fits the viewport — the header stays sticky,
  // rows scroll inside, and the pagination footer stays pinned at the bottom.
  const scrollRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const [maxHeight, setMaxHeight] = useState('60vh');
  useEffect(() => {
    function update() {
      const el = scrollRef.current;
      if (!el) return;
      const docTop = el.getBoundingClientRect().top + window.scrollY;
      const footerH = footerRef.current?.getBoundingClientRect().height ?? 0;
      setMaxHeight(`${Math.max(160, window.innerHeight - docTop - footerH - 8)}px`);
    }
    update();
    const r = requestAnimationFrame(update);
    window.addEventListener('resize', update);
    return () => { cancelAnimationFrame(r); window.removeEventListener('resize', update); };
  }, [total, loading]);

  return (
    <div className="overflow-hidden rounded-card border border-[var(--border-color)] bg-[var(--card-bg)] backdrop-blur-xl">
      <div className={`flex items-center justify-between gap-2 border-b border-[var(--border-color)] ${TABLE_BAR_DENSITY}`}>
        <span className="text-sm font-extrabold text-[var(--text-primary)]">{title}</span>
        {count && <span className="text-xs text-[var(--text-muted)]">{count}</span>}
      </div>
      <div
        ref={scrollRef}
        className="overflow-auto [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-[var(--border-color)]"
        style={{ maxHeight }}
      >
        <table className="w-full border-collapse">
          <thead className="sticky top-0 z-10">
            <tr>
              {columns.map((c) => (
                <th key={c.key} className={HEAD} style={{ textAlign: c.align ?? 'left' }}>{c.header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={columns.length} className="px-4 py-8 text-center text-sm text-[var(--text-muted)]">Loading…</td></tr>
            )}
            {!loading && shown.length === 0 && (
              <tr><td colSpan={columns.length} className="px-4 py-10 text-center text-sm text-[var(--text-muted)]">No records found</td></tr>
            )}
            {!loading && paged.map((r, i) => (
              <tr
                key={rowKey(r)}
                className={`border-b border-[var(--border-color)] transition-colors hover:bg-[var(--bg-secondary)] ${i % 2 === 1 ? 'bg-[var(--bg-secondary)]/60' : ''}`}
              >
                {columns.map((c) => (
                  <td key={c.key} className={CELL} style={{ textAlign: c.align ?? 'left' }}>{c.cell(r)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!loading && total > 0 && (
        <div ref={footerRef} className="border-t border-[var(--border-color)] px-3 py-2 sm:px-5">
          <Pagination page={pageSafe} pageSize={pageSize} total={total} setPage={setPage} setPageSize={setPageSize} />
        </div>
      )}
    </div>
  );
}
